// CollectionTabs.jsx - Tab navigation for collections
import React, { useState } from 'react';
import { Share2, Info } from "lucide-react";
import PlayListButton from "../common/PlayListButton";

/**
 * Tab navigation component for switching between
 * user's collection and shared collections
 */
function CollectionTabs({
  tabs = [],
  activeTab,
  onTabChange,
  tabCounts = {},
  user,
  playListCount = 0,
  onOpenPlayList,
}) {
  const isAnonymous = !user || !user.id;
  const [showMyLibraryInfo, setShowMyLibraryInfo] = useState(false);

  if (tabs.length <= 1) {
    return null; // Don't show tabs if only user's collection exists
  }

  return (
    <>
      <div
        className="collection-tabs"
        style={{
          display: 'flex',
          backgroundColor: 'var(--bg-secondary, #1e1e1e)',
          borderRadius: '8px 8px 0 0',
          margin: '0 0.5rem',
          padding: '0.25rem',
          gap: '0.25rem',
          overflowX: 'auto',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          WebkitScrollbar: { display: 'none' },
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
      {/* Tabs container */}
      <div
        style={{
          display: 'flex',
          gap: '0.25rem',
          overflowX: 'auto',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          WebkitScrollbar: { display: 'none' },
          flex: 1,
          minWidth: 0
        }}
      >
        {tabs.map((tab) => {
          const isActive = activeTab === tab.id;
          const count = tabCounts[tab.id] || 0;

          return (
            <React.Fragment key={tab.id}>
              <button
              onClick={() => onTabChange(tab.id)}
              className={`collection-tab ${isActive ? 'active' : ''}`}
              style={{
                flex: '0 0 auto',
                padding: '0.75rem 1rem',
                border: 'none',
                borderRadius: '6px',
                backgroundColor: isActive
                  ? 'var(--pista-green-primary, #2E7D32)'
                  : 'transparent',
                color: isActive
                  ? 'var(--pista-button-text, white)'
                  : 'var(--text-secondary, #666)',
                fontSize: '0.9rem',
                fontWeight: isActive ? '600' : '500',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                minWidth: 'fit-content',
                whiteSpace: 'nowrap',
                position: 'relative'
              }}
              onMouseEnter={(e) => {
                if (!isActive) {
                  e.target.style.backgroundColor = 'var(--bg-tertiary, #2a2a2a)';
                  e.target.style.color = 'var(--text-primary, #333)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isActive) {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.color = 'var(--text-secondary, #666)';
                }
              }}
              title={tab.type === 'shared' && tab.ownerUsername
                ? `${tab.name} (from ${tab.ownerUsername})`
                : tab.name
              }
            >
              <span>{tab.name}</span>
              {count > 0 && (
                <span
                  style={{
                    backgroundColor: isActive
                      ? 'rgba(255, 255, 255, 0.2)'
                      : 'var(--bg-tertiary, #2a2a2a)',
                    color: isActive
                      ? 'var(--pista-button-text, white)'
                      : 'var(--text-secondary, #666)',
                    fontSize: '0.75rem',
                    fontWeight: '400',
                    padding: '0.125rem 0.375rem',
                    borderRadius: '10px',
                    minWidth: '20px',
                    textAlign: 'center'
                  }}
                >
                  {count}
                </span>
              )}
              {tab.type === 'shared' && (
                <Share2 size={12} style={{ opacity: 0.7, flexShrink: 0 }} />
              )}
            </button>
            {tab.name === 'My Library' && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowMyLibraryInfo(!showMyLibraryInfo);
                }}
                aria-label="My Library info"
                title="What is My Library?"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '0.25rem',
                  marginLeft: '0.25rem',
                  background: 'transparent',
                  border: 'none',
                  color: 'var(--text-secondary, #666)',
                  cursor: 'pointer',
                  borderRadius: '50%'
                }}
              >
                <Info size={14} />
              </button>
            )}
          </React.Fragment>
          );
        })}
      </div>

      {/* Play List icon - right anchored */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.25rem',
          marginLeft: '0.5rem',
          padding: '0.25rem 0.5rem',
          borderRadius: '6px',
          backgroundColor: 'var(--bg-tertiary, #2a2a2a)',
        }}
      >
        <PlayListButton
          isActive={false}
          isAnonymous={isAnonymous || playListCount === 0}
          onClick={onOpenPlayList}
          title={!isAnonymous && playListCount === 0 ? 'Play List is empty' : undefined}
          className="collection-tabs-playlist-button"
          dataTestId="collection-tabs-playlist-button"
        />
        <span
          style={{
            fontSize: '0.75rem',
            fontWeight: '500',
            color: 'var(--text-secondary, #666)',
            minWidth: '18px',
            textAlign: 'center',
          }}
          data-testid="collection-tabs-playlist-count"
        >
          ({isAnonymous ? 0 : playListCount})
        </span>
      </div>
    </div>
    {showMyLibraryInfo && (
      <div
        style={{
          margin: '0 0.5rem 0.5rem 0.5rem',
          padding: '0.5rem 0.75rem',
          backgroundColor: 'var(--bg-tertiary, #3d3d3d)',
          border: '1px solid var(--border-color, #404040)',
          borderRadius: '0 0 8px 8px',
          fontSize: '0.85rem',
          color: 'var(--text-primary, #e0e0e0)',
          textAlign: 'center'
        }}
      >
        Your personal board game shelf - the games you have, are interested in, or have played live here.
      </div>
    )}
  </>
  );
}

export default CollectionTabs;
